import { queryOptions } from '@tanstack/react-query';
import { getUser, listPermissions, listRoles, listUserInvites, listUsers } from '@/api/generated/sdk.gen';
import type { Role, ScopeDto, User, UserDetail, UserGrant, UserInvite, UserRoleAssignment } from '@/api/generated/types.gen';

export type { Role, ScopeDto, User, UserDetail, UserGrant, UserInvite, UserRoleAssignment };

export interface UsersQueryParams {
  q?: string;
  includeDisabled?: boolean;
}

// usersQuery — the operator user list (Settings → Users table). Search and the
// "include disabled" toggle both round-trip to the server.
export function usersQuery({ q, includeDisabled }: UsersQueryParams = {}) {
  return queryOptions({
    queryKey: ['users', { q: q ?? '', includeDisabled: includeDisabled ?? false }] as const,
    queryFn: async ({ signal }): Promise<User[]> => {
      const res = await listUsers({
        throwOnError: true,
        signal,
        query: { q: q || undefined, include_disabled: includeDisabled || undefined },
      });
      return res.data.users;
    },
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && failureCount < 1;
    },
    staleTime: 30_000,
  });
}

// userQuery — a single user's detail (profile + role assignments with scope).
export function userQuery(id: string) {
  return queryOptions({
    queryKey: ['users', id] as const,
    queryFn: async ({ signal }): Promise<UserDetail> => {
      const res = await getUser({ throwOnError: true, signal, path: { id } });
      return res.data;
    },
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && status !== 404 && failureCount < 1;
    },
    staleTime: 30_000,
  });
}

// rolesQuery — the RBAC role catalogue, used to populate the invite dialog's
// role picker with real role codes (never a hardcoded guess).
export const rolesQuery = queryOptions({
  queryKey: ['roles'] as const,
  queryFn: async ({ signal }): Promise<Role[]> => {
    const res = await listRoles({ throwOnError: true, signal });
    return res.data.roles;
  },
  retry: (failureCount, err) => {
    const status = (err as { status?: number })?.status;
    return status !== 401 && failureCount < 1;
  },
  staleTime: 60_000,
});

// permissionsQuery — the full canonical-action catalogue, used to populate
// the direct-grant dialog's action picker with real actions (never a guess).
export const permissionsQuery = queryOptions({
  queryKey: ['permissions'] as const,
  queryFn: async ({ signal }): Promise<string[]> => {
    const res = await listPermissions({ throwOnError: true, signal });
    return res.data.permissions;
  },
  retry: (failureCount, err) => {
    const status = (err as { status?: number })?.status;
    return status !== 401 && failureCount < 1;
  },
  staleTime: 60_000,
});

// invitesQuery — pending user invites (Users → Pending invites section).
export const invitesQuery = queryOptions({
  queryKey: ['user-invites'] as const,
  queryFn: async ({ signal }): Promise<UserInvite[]> => {
    const res = await listUserInvites({ throwOnError: true, signal });
    return res.data.invites;
  },
  retry: (failureCount, err) => {
    const status = (err as { status?: number })?.status;
    return status !== 401 && failureCount < 1;
  },
  staleTime: 30_000,
});
